/**
 * ~500 most common ICD-10-CM codes used in home health nursing.
 * Organized by category for maintainability.
 * Used as the primary local search source — fast, offline, instant.
 */

export interface ICD10Code {
    code: string;
    description: string;
}

export const COMMON_ICD10_CODES: ICD10Code[] = [
    // ── Endocrine / Metabolic ──────────────────────────────
    {
        code: "E08.9",
        description: "Diabetes mellitus due to underlying condition without complications"
    },
    {
        code: "E09.9",
        description: "Drug or chemical induced diabetes mellitus without complications"
    },
    { code: "E10.9", description: "Type 1 diabetes mellitus without complications" },
    { code: "E10.65", description: "Type 1 diabetes mellitus with hyperglycemia" },
    { code: "E10.10", description: "Type 1 diabetes mellitus with ketoacidosis without coma" },
    { code: "E11.9", description: "Type 2 diabetes mellitus without complications" },
    { code: "E11.65", description: "Type 2 diabetes mellitus with hyperglycemia" },
    { code: "E11.21", description: "Type 2 diabetes mellitus with diabetic nephropathy" },
    {
        code: "E11.22",
        description: "Type 2 diabetes mellitus with diabetic chronic kidney disease"
    },
    {
        code: "E11.29",
        description: "Type 2 diabetes mellitus with other diabetic kidney complication"
    },
    {
        code: "E11.311",
        description: "Type 2 diabetes with unspecified diabetic retinopathy with macular edema"
    },
    {
        code: "E11.319",
        description: "Type 2 diabetes with unspecified diabetic retinopathy without macular edema"
    },
    { code: "E11.36", description: "Type 2 diabetes mellitus with diabetic arthropathy" },
    {
        code: "E11.40",
        description: "Type 2 diabetes mellitus with diabetic neuropathy, unspecified"
    },
    { code: "E11.42", description: "Type 2 diabetes mellitus with diabetic polyneuropathy" },
    {
        code: "E11.51",
        description: "Type 2 diabetes mellitus with diabetic peripheral angiopathy without gangrene"
    },
    {
        code: "E11.52",
        description: "Type 2 diabetes mellitus with diabetic peripheral angiopathy with gangrene"
    },
    {
        code: "E11.59",
        description: "Type 2 diabetes mellitus with other circulatory complications"
    },
    {
        code: "E11.610",
        description: "Type 2 diabetes mellitus with diabetic neuropathic arthropathy"
    },
    { code: "E11.618", description: "Type 2 diabetes mellitus with other diabetic arthropathy" },
    { code: "E11.620", description: "Type 2 diabetes mellitus with diabetic dermatitis" },
    { code: "E11.621", description: "Type 2 diabetes mellitus with foot ulcer" },
    { code: "E11.622", description: "Type 2 diabetes mellitus with other skin ulcer" },
    { code: "E11.628", description: "Type 2 diabetes mellitus with other skin complications" },
    { code: "E11.630", description: "Type 2 diabetes mellitus with periodontal disease" },
    { code: "E11.649", description: "Type 2 diabetes mellitus with hypoglycemia without coma" },
    { code: "E11.69", description: "Type 2 diabetes mellitus with other specified complication" },
    { code: "E11.8", description: "Type 2 diabetes mellitus with unspecified complications" },
    { code: "E13.9", description: "Other specified diabetes mellitus without complications" },
    { code: "E03.9", description: "Hypothyroidism, unspecified" },
    { code: "E04.1", description: "Nontoxic single thyroid nodule" },
    { code: "E04.9", description: "Nontoxic goiter, unspecified" },
    {
        code: "E05.90",
        description: "Thyrotoxicosis, unspecified without thyrotoxic crisis or storm"
    },
    { code: "E06.3", description: "Autoimmune thyroiditis" },
    { code: "E07.9", description: "Disorder of thyroid, unspecified" },
    { code: "E28.2", description: "Polycystic ovarian syndrome" },
    { code: "E55.9", description: "Vitamin D deficiency, unspecified" },
    { code: "E56.1", description: "Deficiency of vitamin K" },
    { code: "E61.1", description: "Iron deficiency" },
    { code: "E66.01", description: "Morbid (severe) obesity due to excess calories" },
    { code: "E66.09", description: "Other obesity due to excess calories" },
    { code: "E66.9", description: "Obesity, unspecified" },
    { code: "E78.0", description: "Pure hypercholesterolemia, unspecified" },
    { code: "E78.00", description: "Pure hypercholesterolemia, unspecified" },
    { code: "E78.1", description: "Pure hyperglyceridemia" },
    { code: "E78.2", description: "Mixed hyperlipidemia" },
    { code: "E78.5", description: "Hyperlipidemia, unspecified" },
    {
        code: "E79.0",
        description: "Hyperuricemia without signs of inflammatory arthritis and tophaceous disease"
    },
    { code: "E83.42", description: "Hypomagnesemia" },
    { code: "E83.51", description: "Hypocalcemia" },
    { code: "E83.52", description: "Hypercalcemia" },
    { code: "E86.0", description: "Dehydration" },
    { code: "E86.1", description: "Hypovolemia" },
    { code: "E87.0", description: "Hyperosmolality and hypernatremia" },
    { code: "E87.1", description: "Hypo-osmolality and hyponatremia" },
    { code: "E87.2", description: "Acidosis" },
    { code: "E87.5", description: "Hyperkalemia" },
    { code: "E87.6", description: "Hypokalemia" },

    // ── Mental / Behavioral ────────────────────────────────
    { code: "F01.50", description: "Vascular dementia without behavioral disturbance" },
    { code: "F01.51", description: "Vascular dementia with behavioral disturbance" },
    {
        code: "F02.80",
        description:
            "Dementia in other diseases classified elsewhere without behavioral disturbance"
    },
    {
        code: "F02.81",
        description: "Dementia in other diseases classified elsewhere with behavioral disturbance"
    },
    { code: "F03.90", description: "Unspecified dementia without behavioral disturbance" },
    { code: "F03.91", description: "Unspecified dementia with behavioral disturbance" },
    { code: "F10.20", description: "Alcohol dependence, uncomplicated" },
    { code: "F17.210", description: "Nicotine dependence, cigarettes, uncomplicated" },
    { code: "F20.9", description: "Schizophrenia, unspecified" },
    { code: "F25.0", description: "Schizoaffective disorder, bipolar type" },
    { code: "F25.1", description: "Schizoaffective disorder, depressive type" },
    { code: "F31.9", description: "Bipolar disorder, unspecified" },
    { code: "F32.0", description: "Major depressive disorder, single episode, mild" },
    { code: "F32.1", description: "Major depressive disorder, single episode, moderate" },
    {
        code: "F32.2",
        description: "Major depressive disorder, single episode, severe without psychotic features"
    },
    { code: "F32.9", description: "Major depressive disorder, single episode, unspecified" },
    { code: "F33.0", description: "Major depressive disorder, recurrent, mild" },
    { code: "F33.1", description: "Major depressive disorder, recurrent, moderate" },
    {
        code: "F33.2",
        description: "Major depressive disorder, recurrent severe without psychotic features"
    },
    { code: "F33.9", description: "Major depressive disorder, recurrent, unspecified" },
    { code: "F34.1", description: "Dysthymic disorder" },
    { code: "F39", description: "Unspecified mood [affective] disorder" },
    { code: "F40.10", description: "Social phobia, unspecified" },
    { code: "F41.0", description: "Panic disorder without agoraphobia" },
    { code: "F41.1", description: "Generalized anxiety disorder" },
    { code: "F41.9", description: "Anxiety disorder, unspecified" },
    { code: "F42.9", description: "Obsessive-compulsive disorder, unspecified" },
    { code: "F43.10", description: "Post-traumatic stress disorder, unspecified" },
    { code: "F43.20", description: "Adjustment disorder, unspecified" },
    { code: "F43.23", description: "Adjustment disorder with mixed anxiety and depressed mood" },
    { code: "F51.01", description: "Primary insomnia" },

    // ── Nervous System ─────────────────────────────────────
    { code: "G20", description: "Parkinson's disease" },
    { code: "G25.0", description: "Essential tremor" },
    { code: "G25.81", description: "Restless legs syndrome" },
    { code: "G30.0", description: "Alzheimer's disease with early onset" },
    { code: "G30.1", description: "Alzheimer's disease with late onset" },
    { code: "G30.9", description: "Alzheimer's disease, unspecified" },
    { code: "G31.84", description: "Mild cognitive impairment" },
    { code: "G35", description: "Multiple sclerosis" },
    {
        code: "G40.909",
        description: "Epilepsy, unspecified, not intractable, without status epilepticus"
    },
    {
        code: "G43.909",
        description: "Migraine, unspecified, not intractable, without status migrainosus"
    },
    { code: "G45.9", description: "Transient cerebral ischemic attack, unspecified" },
    { code: "G47.00", description: "Insomnia, unspecified" },
    { code: "G47.33", description: "Obstructive sleep apnea" },
    { code: "G47.9", description: "Sleep disorder, unspecified" },
    { code: "G54.1", description: "Lumbosacral plexus disorders" },
    { code: "G56.00", description: "Carpal tunnel syndrome, unspecified upper limb" },
    { code: "G62.9", description: "Polyneuropathy, unspecified" },
    { code: "G89.29", description: "Other chronic pain" },
    { code: "G89.4", description: "Chronic pain syndrome" },

    // ── Eye / Ear ──────────────────────────────────────────
    { code: "H25.9", description: "Unspecified age-related cataract" },
    { code: "H26.9", description: "Unspecified cataract" },
    {
        code: "H33.001",
        description: "Unspecified retinal detachment with retinal break, right eye"
    },
    { code: "H35.30", description: "Unspecified macular degeneration" },
    { code: "H40.10X0", description: "Unspecified open-angle glaucoma" },
    { code: "H40.9", description: "Unspecified glaucoma" },
    { code: "H54.7", description: "Unspecified visual loss" },
    { code: "H61.20", description: "Impacted cerumen, unspecified ear" },
    { code: "H90.5", description: "Unspecified sensorineural hearing loss" },
    { code: "H91.90", description: "Unspecified hearing loss, unspecified ear" },
    { code: "H93.19", description: "Tinnitus, unspecified ear" },

    // ── Circulatory / Cardiovascular ───────────────────────
    { code: "I10", description: "Essential (primary) hypertension" },
    { code: "I11.0", description: "Hypertensive heart disease with heart failure" },
    { code: "I11.9", description: "Hypertensive heart disease without heart failure" },
    {
        code: "I12.9",
        description: "Hypertensive chronic kidney disease with stage 1-4 or unspecified CKD"
    },
    {
        code: "I13.0",
        description:
            "Hypertensive heart and chronic kidney disease with heart failure and stage 1-4 CKD"
    },
    {
        code: "I13.10",
        description:
            "Hypertensive heart and chronic kidney disease without heart failure, stage 1-4 CKD"
    },
    { code: "I20.0", description: "Unstable angina" },
    { code: "I20.9", description: "Angina pectoris, unspecified" },
    { code: "I21.9", description: "Acute myocardial infarction, unspecified" },
    {
        code: "I25.10",
        description:
            "Atherosclerotic heart disease of native coronary artery without angina pectoris"
    },
    {
        code: "I25.110",
        description: "Atherosclerotic heart disease of native coronary artery with unstable angina"
    },
    {
        code: "I25.119",
        description:
            "Atherosclerotic heart disease of native coronary artery with unspecified angina"
    },
    { code: "I25.2", description: "Old myocardial infarction" },
    { code: "I25.5", description: "Ischemic cardiomyopathy" },
    { code: "I25.9", description: "Chronic ischemic heart disease, unspecified" },
    { code: "I27.0", description: "Primary pulmonary hypertension" },
    { code: "I27.20", description: "Pulmonary hypertension, unspecified" },
    { code: "I34.0", description: "Nonrheumatic mitral (valve) insufficiency" },
    { code: "I35.0", description: "Nonrheumatic aortic (valve) stenosis" },
    { code: "I35.1", description: "Nonrheumatic aortic (valve) insufficiency" },
    { code: "I42.0", description: "Dilated cardiomyopathy" },
    { code: "I42.9", description: "Cardiomyopathy, unspecified" },
    { code: "I44.1", description: "Atrioventricular block, second degree" },
    { code: "I44.2", description: "Atrioventricular block, complete" },
    { code: "I47.1", description: "Supraventricular tachycardia" },
    { code: "I47.2", description: "Ventricular tachycardia" },
    { code: "I48.0", description: "Paroxysmal atrial fibrillation" },
    { code: "I48.1", description: "Persistent atrial fibrillation" },
    { code: "I48.2", description: "Chronic atrial fibrillation" },
    { code: "I48.91", description: "Unspecified atrial fibrillation" },
    { code: "I49.9", description: "Cardiac arrhythmia, unspecified" },
    { code: "I50.1", description: "Left ventricular failure, unspecified" },
    { code: "I50.20", description: "Unspecified systolic (congestive) heart failure" },
    { code: "I50.22", description: "Chronic systolic (congestive) heart failure" },
    { code: "I50.30", description: "Unspecified diastolic (congestive) heart failure" },
    { code: "I50.32", description: "Chronic diastolic (congestive) heart failure" },
    { code: "I50.40", description: "Unspecified combined systolic and diastolic heart failure" },
    { code: "I50.42", description: "Chronic combined systolic and diastolic heart failure" },
    { code: "I50.9", description: "Heart failure, unspecified" },
    { code: "I63.9", description: "Cerebral infarction, unspecified" },
    { code: "I65.29", description: "Occlusion and stenosis of unspecified carotid artery" },
    { code: "I67.9", description: "Cerebrovascular disease, unspecified" },
    { code: "I69.30", description: "Unspecified sequelae of cerebral infarction" },
    { code: "I69.398", description: "Other sequelae of cerebral infarction" },
    { code: "I70.0", description: "Atherosclerosis of aorta" },
    {
        code: "I70.201",
        description: "Unspecified atherosclerosis of native arteries of extremities, right leg"
    },
    {
        code: "I70.209",
        description: "Unspecified atherosclerosis of native arteries of extremities, unspecified"
    },
    { code: "I73.9", description: "Peripheral vascular disease, unspecified" },
    { code: "I77.1", description: "Stricture of artery" },
    { code: "I80.10", description: "Phlebitis and thrombophlebitis of unspecified femoral vein" },
    {
        code: "I82.401",
        description:
            "Acute embolism and thrombosis of unspecified deep veins of right lower extremity"
    },
    {
        code: "I82.409",
        description:
            "Acute embolism and thrombosis of unspecified deep veins of unspecified lower extremity"
    },
    { code: "I83.90", description: "Asymptomatic varicose veins of unspecified lower extremity" },
    { code: "I87.2", description: "Venous insufficiency (chronic) (peripheral)" },
    { code: "I95.1", description: "Orthostatic hypotension" },
    { code: "I95.9", description: "Hypotension, unspecified" },
    {
        code: "I97.110",
        description: "Postprocedural cardiac insufficiency following cardiac surgery"
    },
    {
        code: "I97.191",
        description: "Other postprocedural cardiac functional disturbances following other surgery"
    },

    // ── Respiratory ────────────────────────────────────────
    { code: "J02.9", description: "Acute pharyngitis, unspecified" },
    { code: "J06.9", description: "Acute upper respiratory infection, unspecified" },
    { code: "J18.9", description: "Pneumonia, unspecified organism" },
    { code: "J20.9", description: "Acute bronchitis, unspecified" },
    { code: "J30.9", description: "Allergic rhinitis, unspecified" },
    { code: "J32.9", description: "Chronic sinusitis, unspecified" },
    { code: "J34.2", description: "Deviated nasal septum" },
    { code: "J39.0", description: "Retropharyngeal and parapharyngeal abscess" },
    { code: "J40", description: "Bronchitis, not specified as acute or chronic" },
    { code: "J42", description: "Unspecified chronic bronchitis" },
    { code: "J43.9", description: "Emphysema, unspecified" },
    { code: "J44.0", description: "COPD with (acute) lower respiratory infection" },
    { code: "J44.1", description: "COPD with (acute) exacerbation" },
    { code: "J44.9", description: "COPD, unspecified" },
    { code: "J45.20", description: "Mild intermittent asthma, uncomplicated" },
    { code: "J45.30", description: "Mild persistent asthma, uncomplicated" },
    { code: "J45.40", description: "Moderate persistent asthma, uncomplicated" },
    { code: "J45.50", description: "Severe persistent asthma, uncomplicated" },
    { code: "J45.909", description: "Unspecified asthma, uncomplicated" },
    { code: "J84.10", description: "Pulmonary fibrosis, unspecified" },
    { code: "J90", description: "Pleural effusion, not elsewhere classified" },
    {
        code: "J96.10",
        description: "Chronic respiratory failure, unspecified whether with hypoxia or hypercapnia"
    },
    { code: "J96.11", description: "Chronic respiratory failure with hypoxia" },
    { code: "J98.11", description: "Atelectasis" },

    // ── Digestive ──────────────────────────────────────────
    { code: "K02.9", description: "Dental caries, unspecified" },
    { code: "K04.7", description: "Periapical abscess without sinus" },
    {
        code: "K08.109",
        description: "Complete loss of teeth, unspecified cause, unspecified class"
    },
    { code: "K12.1", description: "Other forms of stomatitis" },
    { code: "K13.0", description: "Diseases of lips" },
    { code: "K21.0", description: "Gastro-esophageal reflux disease with esophagitis" },
    { code: "K21.9", description: "Gastro-esophageal reflux disease without esophagitis" },
    { code: "K25.9", description: "Gastric ulcer, unspecified, without hemorrhage or perforation" },
    { code: "K29.70", description: "Gastritis, unspecified, without bleeding" },
    { code: "K30", description: "Functional dyspepsia" },
    { code: "K31.84", description: "Gastroparesis" },
    { code: "K35.80", description: "Unspecified acute appendicitis" },
    {
        code: "K40.90",
        description:
            "Unilateral inguinal hernia, without obstruction or gangrene, not specified as recurrent"
    },
    { code: "K44.9", description: "Diaphragmatic hernia without obstruction or gangrene" },
    { code: "K50.90", description: "Crohn's disease, unspecified, without complications" },
    { code: "K51.90", description: "Ulcerative colitis, unspecified, without complications" },
    {
        code: "K57.90",
        description:
            "Diverticulosis of intestine, unspecified, without perforation or abscess, without bleeding"
    },
    { code: "K58.9", description: "Irritable bowel syndrome without diarrhea" },
    { code: "K59.00", description: "Constipation, unspecified" },
    { code: "K59.09", description: "Other constipation" },
    { code: "K64.9", description: "Unspecified hemorrhoids" },
    { code: "K70.30", description: "Alcoholic cirrhosis of liver without ascites" },
    { code: "K74.60", description: "Unspecified cirrhosis of liver" },
    { code: "K76.0", description: "Fatty (change of) liver, not elsewhere classified" },
    {
        code: "K80.20",
        description: "Calculus of gallbladder without cholecystitis without obstruction"
    },
    {
        code: "K85.90",
        description: "Acute pancreatitis without necrosis or infection, unspecified"
    },
    { code: "K86.1", description: "Other chronic pancreatitis" },
    { code: "K92.0", description: "Hematemesis" },
    { code: "K92.1", description: "Melena" },
    { code: "K92.2", description: "Gastrointestinal hemorrhage, unspecified" },

    // ── Skin ───────────────────────────────────────────────
    { code: "L02.91", description: "Cutaneous abscess, unspecified" },
    { code: "L03.90", description: "Cellulitis, unspecified" },
    {
        code: "L08.9",
        description: "Local infection of the skin and subcutaneous tissue, unspecified"
    },
    { code: "L20.9", description: "Atopic dermatitis, unspecified" },
    { code: "L21.9", description: "Seborrheic dermatitis, unspecified" },
    { code: "L23.9", description: "Allergic contact dermatitis, unspecified cause" },
    { code: "L25.9", description: "Unspecified contact dermatitis, unspecified cause" },
    { code: "L29.9", description: "Pruritus, unspecified" },
    { code: "L30.9", description: "Dermatitis, unspecified" },
    { code: "L40.0", description: "Psoriasis vulgaris" },
    { code: "L50.9", description: "Urticaria, unspecified" },
    { code: "L57.0", description: "Actinic keratosis" },
    { code: "L72.0", description: "Epidermal cyst" },
    { code: "L89.009", description: "Pressure ulcer of unspecified elbow, unspecified stage" },
    {
        code: "L89.109",
        description: "Pressure ulcer of unspecified part of back, unspecified stage"
    },
    { code: "L89.159", description: "Pressure ulcer of sacral region, unspecified stage" },
    { code: "L89.209", description: "Pressure ulcer of unspecified hip, unspecified stage" },
    { code: "L89.309", description: "Pressure ulcer of unspecified buttock, unspecified stage" },
    { code: "L89.90", description: "Pressure ulcer of unspecified site, unspecified stage" },
    {
        code: "L97.109",
        description: "Non-pressure chronic ulcer of unspecified thigh with unspecified severity"
    },
    {
        code: "L97.509",
        description:
            "Non-pressure chronic ulcer of other part of unspecified foot with unspecified severity"
    },
    {
        code: "L97.909",
        description:
            "Non-pressure chronic ulcer of unspecified part of unspecified lower leg with unspecified severity"
    },

    // ── Musculoskeletal ────────────────────────────────────
    { code: "M06.9", description: "Rheumatoid arthritis, unspecified" },
    { code: "M10.9", description: "Gout, unspecified" },
    { code: "M15.0", description: "Primary generalized (osteo)arthritis" },
    { code: "M16.9", description: "Osteoarthritis of hip, unspecified" },
    { code: "M17.9", description: "Osteoarthritis of knee, unspecified" },
    { code: "M19.90", description: "Unspecified osteoarthritis, unspecified site" },
    { code: "M25.50", description: "Pain in unspecified joint" },
    { code: "M25.511", description: "Pain in right shoulder" },
    { code: "M25.512", description: "Pain in left shoulder" },
    { code: "M25.551", description: "Pain in right hip" },
    { code: "M25.552", description: "Pain in left hip" },
    { code: "M25.561", description: "Pain in right knee" },
    { code: "M25.562", description: "Pain in left knee" },
    { code: "M32.9", description: "Systemic lupus erythematosus, unspecified" },
    { code: "M34.9", description: "Systemic sclerosis, unspecified" },
    { code: "M41.9", description: "Scoliosis, unspecified" },
    { code: "M43.16", description: "Spondylolisthesis, lumbar region" },
    {
        code: "M47.816",
        description: "Spondylosis without myelopathy or radiculopathy, lumbar region"
    },
    {
        code: "M47.817",
        description: "Spondylosis without myelopathy or radiculopathy, lumbosacral region"
    },
    { code: "M48.06", description: "Spinal stenosis, lumbar region" },
    {
        code: "M50.30",
        description: "Other cervical disc degeneration, unspecified cervical region"
    },
    {
        code: "M51.16",
        description: "Intervertebral disc disorders with radiculopathy, lumbar region"
    },
    { code: "M54.2", description: "Cervicalgia" },
    { code: "M54.30", description: "Sciatica, unspecified side" },
    { code: "M54.5", description: "Low back pain" },
    { code: "M54.9", description: "Dorsalgia, unspecified" },
    { code: "M62.81", description: "Muscle weakness (generalized)" },
    { code: "M62.838", description: "Muscle spasm of other muscle" },
    {
        code: "M75.10",
        description: "Unspecified rotator cuff tear or rupture, not specified as traumatic"
    },
    { code: "M79.1", description: "Myalgia" },
    { code: "M79.3", description: "Panniculitis, unspecified" },
    { code: "M79.7", description: "Fibromyalgia" },
    {
        code: "M80.00XA",
        description:
            "Age-related osteoporosis with current pathological fracture, unspecified site, initial encounter"
    },
    {
        code: "M81.0",
        description: "Age-related osteoporosis without current pathological fracture"
    },
    { code: "M84.40XA", description: "Pathological fracture, unspecified site, initial encounter" },

    // ── Genitourinary ──────────────────────────────────────
    {
        code: "N12",
        description: "Tubulo-interstitial nephritis, not specified as acute or chronic"
    },
    { code: "N13.30", description: "Unspecified hydronephrosis" },
    { code: "N17.9", description: "Acute kidney failure, unspecified" },
    { code: "N18.1", description: "Chronic kidney disease, stage 1" },
    { code: "N18.2", description: "Chronic kidney disease, stage 2 (mild)" },
    { code: "N18.3", description: "Chronic kidney disease, stage 3 (moderate)" },
    { code: "N18.30", description: "Chronic kidney disease, stage 3 unspecified" },
    { code: "N18.31", description: "Chronic kidney disease, stage 3a" },
    { code: "N18.32", description: "Chronic kidney disease, stage 3b" },
    { code: "N18.4", description: "Chronic kidney disease, stage 4 (severe)" },
    { code: "N18.5", description: "Chronic kidney disease, stage 5" },
    { code: "N18.6", description: "End stage renal disease" },
    { code: "N18.9", description: "Chronic kidney disease, unspecified" },
    { code: "N19", description: "Unspecified kidney failure" },
    { code: "N20.0", description: "Calculus of kidney" },
    { code: "N30.00", description: "Acute cystitis without hematuria" },
    { code: "N31.9", description: "Neuromuscular dysfunction of bladder, unspecified" },
    { code: "N32.81", description: "Overactive bladder" },
    { code: "N39.0", description: "Urinary tract infection, site not specified" },
    { code: "N39.41", description: "Urge incontinence" },
    { code: "N39.46", description: "Mixed incontinence" },
    { code: "N39.498", description: "Other specified urinary incontinence" },
    {
        code: "N40.0",
        description: "Benign prostatic hyperplasia without lower urinary tract symptoms"
    },
    {
        code: "N40.1",
        description: "Benign prostatic hyperplasia with lower urinary tract symptoms"
    },
    { code: "N80.9", description: "Endometriosis, unspecified" },
    { code: "N95.1", description: "Menopausal and female climacteric states" },

    // ── Injury / Falls / Fractures ─────────────────────────
    { code: "R29.6", description: "Repeated falls" },
    {
        code: "S01.90XA",
        description: "Unspecified open wound of unspecified part of head, initial encounter"
    },
    {
        code: "S06.0X0A",
        description: "Concussion without loss of consciousness, initial encounter"
    },
    {
        code: "S22.009A",
        description: "Unspecified fracture of unspecified thoracic vertebra, initial encounter"
    },
    {
        code: "S32.009A",
        description: "Unspecified fracture of unspecified lumbar vertebra, initial encounter"
    },
    {
        code: "S42.009A",
        description: "Fracture of unspecified part of unspecified clavicle, initial encounter"
    },
    {
        code: "S52.509A",
        description:
            "Unspecified fracture of the lower end of unspecified radius, initial encounter"
    },
    {
        code: "S72.009A",
        description: "Fracture of unspecified part of neck of unspecified femur, initial encounter"
    },
    {
        code: "S72.90XA",
        description: "Unspecified fracture of unspecified femur, initial encounter"
    },
    {
        code: "S82.009A",
        description: "Unspecified fracture of unspecified patella, initial encounter"
    },
    {
        code: "S82.90XA",
        description: "Unspecified fracture of unspecified lower leg, initial encounter"
    },
    {
        code: "W01.0XXA",
        description:
            "Fall on same level from slipping, tripping and stumbling without subsequent striking, initial encounter"
    },
    { code: "W06.XXXA", description: "Fall from bed, initial encounter" },
    {
        code: "W10.9XXA",
        description: "Fall on and from unspecified stairs and steps, initial encounter"
    },
    { code: "W18.30XA", description: "Fall on same level, unspecified, initial encounter" },
    { code: "W19.XXXA", description: "Unspecified fall, initial encounter" },

    // ── Symptoms / Signs (R codes) ─────────────────────────
    { code: "R00.0", description: "Tachycardia, unspecified" },
    { code: "R00.1", description: "Bradycardia, unspecified" },
    { code: "R00.2", description: "Palpitations" },
    {
        code: "R03.0",
        description: "Elevated blood-pressure reading, without diagnosis of hypertension"
    },
    { code: "R04.0", description: "Epistaxis" },
    { code: "R05.9", description: "Cough, unspecified" },
    { code: "R06.00", description: "Dyspnea, unspecified" },
    { code: "R06.02", description: "Shortness of breath" },
    { code: "R06.09", description: "Other forms of dyspnea" },
    { code: "R07.9", description: "Chest pain, unspecified" },
    { code: "R09.02", description: "Hypoxemia" },
    { code: "R10.9", description: "Unspecified abdominal pain" },
    { code: "R11.0", description: "Nausea" },
    { code: "R11.10", description: "Vomiting, unspecified" },
    { code: "R11.2", description: "Nausea with vomiting, unspecified" },
    { code: "R13.10", description: "Dysphagia, unspecified" },
    { code: "R19.7", description: "Diarrhea, unspecified" },
    { code: "R20.0", description: "Anesthesia of skin" },
    { code: "R20.2", description: "Paresthesia of skin" },
    { code: "R21", description: "Rash and other nonspecific skin eruption" },
    { code: "R22.9", description: "Localized swelling, mass and lump, unspecified" },
    { code: "R26.0", description: "Ataxic gait" },
    { code: "R26.2", description: "Difficulty in walking, not elsewhere classified" },
    { code: "R26.81", description: "Unsteadiness on feet" },
    { code: "R26.89", description: "Other abnormalities of gait and mobility" },
    { code: "R26.9", description: "Unspecified abnormalities of gait and mobility" },
    { code: "R27.0", description: "Ataxia, unspecified" },
    { code: "R31.9", description: "Hematuria, unspecified" },
    { code: "R32", description: "Unspecified urinary incontinence" },
    { code: "R33.9", description: "Retention of urine, unspecified" },
    { code: "R35.0", description: "Frequency of micturition" },
    { code: "R40.20", description: "Unspecified coma" },
    { code: "R41.0", description: "Disorientation, unspecified" },
    { code: "R41.3", description: "Other amnesia" },
    { code: "R41.840", description: "Attention and concentration deficit" },
    { code: "R41.841", description: "Cognitive communication deficit" },
    { code: "R42", description: "Dizziness and giddiness" },
    { code: "R47.01", description: "Aphasia" },
    { code: "R47.1", description: "Dysarthria and anarthria" },
    { code: "R47.89", description: "Other speech disturbances" },
    { code: "R50.9", description: "Fever, unspecified" },
    { code: "R51.9", description: "Headache, unspecified" },
    { code: "R52", description: "Pain, unspecified" },
    { code: "R53.1", description: "Weakness" },
    { code: "R53.81", description: "Other malaise" },
    { code: "R53.83", description: "Other fatigue" },
    { code: "R55", description: "Syncope and collapse" },
    { code: "R56.9", description: "Unspecified convulsions" },
    { code: "R60.0", description: "Localized edema" },
    { code: "R60.9", description: "Edema, unspecified" },
    { code: "R62.7", description: "Adult failure to thrive" },
    { code: "R63.0", description: "Anorexia" },
    { code: "R63.4", description: "Abnormal weight loss" },
    { code: "R63.5", description: "Abnormal weight gain" },
    { code: "R63.6", description: "Underweight" },
    { code: "R68.89", description: "Other general symptoms and signs" },
    { code: "R73.03", description: "Prediabetes" },
    { code: "R73.9", description: "Hyperglycemia, unspecified" },

    // ── Blood / Immune ─────────────────────────────────────
    { code: "D50.9", description: "Iron deficiency anemia, unspecified" },
    { code: "D53.9", description: "Nutritional anemia, unspecified" },
    { code: "D62", description: "Acute posthemorrhagic anemia" },
    { code: "D63.1", description: "Anemia in chronic kidney disease" },
    { code: "D64.9", description: "Anemia, unspecified" },
    { code: "D68.9", description: "Coagulation defect, unspecified" },
    { code: "D69.6", description: "Thrombocytopenia, unspecified" },
    { code: "D72.829", description: "Elevated white blood cell count, unspecified" },
    { code: "D89.9", description: "Disorder involving the immune mechanism, unspecified" },

    // ── Neoplasms ──────────────────────────────────────────
    { code: "C18.9", description: "Malignant neoplasm of colon, unspecified" },
    {
        code: "C34.90",
        description: "Malignant neoplasm of unspecified part of unspecified bronchus or lung"
    },
    {
        code: "C50.919",
        description: "Malignant neoplasm of unspecified site of unspecified female breast"
    },
    { code: "C61", description: "Malignant neoplasm of prostate" },
    { code: "C64.9", description: "Malignant neoplasm of unspecified kidney, except renal pelvis" },
    { code: "C67.9", description: "Malignant neoplasm of bladder, unspecified" },
    { code: "C73", description: "Malignant neoplasm of thyroid gland" },
    { code: "C78.00", description: "Secondary malignant neoplasm of unspecified lung" },
    { code: "C79.51", description: "Secondary malignant neoplasm of bone" },
    { code: "C85.90", description: "Non-Hodgkin lymphoma, unspecified, unspecified site" },
    { code: "C90.00", description: "Multiple myeloma not having achieved remission" },
    { code: "D05.90", description: "Unspecified type of carcinoma in situ of unspecified breast" },
    { code: "D17.9", description: "Benign lipomatous neoplasm, unspecified" },
    { code: "D22.9", description: "Melanocytic nevi, unspecified" },
    { code: "D49.9", description: "Neoplasm of unspecified behavior of unspecified site" },

    // ── Infectious ─────────────────────────────────────────
    { code: "A04.72", description: "Clostridioides difficile enterocolitis, recurrent" },
    { code: "A09", description: "Infectious gastroenteritis and colitis, unspecified" },
    { code: "A41.9", description: "Sepsis, unspecified organism" },
    { code: "A49.9", description: "Bacterial infection, unspecified" },
    { code: "B00.9", description: "Herpesviral infection, unspecified" },
    { code: "B02.9", description: "Zoster without complications" },
    { code: "B19.20", description: "Unspecified viral hepatitis C without hepatic coma" },
    { code: "B35.1", description: "Tinea unguium" },
    { code: "B37.0", description: "Candidal stomatitis" },
    {
        code: "B96.20",
        description: "Unspecified Escherichia coli as the cause of diseases classified elsewhere"
    },
    { code: "U07.1", description: "COVID-19" },

    // ── Aftercare / Status (Z codes) ───────────────────────
    { code: "Z23", description: "Encounter for immunization" },
    { code: "Z46.6", description: "Encounter for fitting and adjustment of urinary device" },
    {
        code: "Z48.812",
        description: "Encounter for surgical aftercare following surgery on the circulatory system"
    },
    { code: "Z48.89", description: "Encounter for other specified surgical aftercare" },
    { code: "Z51.11", description: "Encounter for antineoplastic chemotherapy" },
    { code: "Z51.5", description: "Encounter for palliative care" },
    { code: "Z66", description: "Do not resuscitate" },
    { code: "Z74.01", description: "Bed confinement status" },
    { code: "Z79.01", description: "Long term (current) use of anticoagulants" },
    { code: "Z79.02", description: "Long term (current) use of antithrombotics/antiplatelets" },
    { code: "Z79.4", description: "Long term (current) use of insulin" },
    { code: "Z79.52", description: "Long term (current) use of systemic steroids" },
    { code: "Z79.82", description: "Long term (current) use of aspirin" },
    { code: "Z79.84", description: "Long term (current) use of oral hypoglycemic drugs" },
    { code: "Z79.891", description: "Long term (current) use of opiate analgesic" },
    { code: "Z79.899", description: "Other long term (current) drug therapy" },
    { code: "Z85.3", description: "Personal history of malignant neoplasm of breast" },
    { code: "Z85.46", description: "Personal history of malignant neoplasm of prostate" },
    {
        code: "Z85.819",
        description: "Personal history of malignant neoplasm of unspecified urinary tract organ"
    },
    {
        code: "Z86.73",
        description:
            "Personal history of transient ischemic attack (TIA), and cerebral infarction without residual deficits"
    },
    { code: "Z87.11", description: "Personal history of peptic ulcer disease" },
    {
        code: "Z87.39",
        description:
            "Personal history of other diseases of the musculoskeletal system and connective tissue"
    },
    { code: "Z87.891", description: "Personal history of nicotine dependence" },
    { code: "Z90.10", description: "Acquired absence of breast and nipple, unspecified side" },
    { code: "Z93.0", description: "Tracheostomy status" },
    { code: "Z93.1", description: "Gastrostomy status" },
    { code: "Z93.2", description: "Ileostomy status" },
    { code: "Z93.3", description: "Colostomy status" },
    { code: "Z93.6", description: "Other artificial openings of urinary tract status" },
    { code: "Z95.0", description: "Presence of cardiac pacemaker" },
    { code: "Z95.1", description: "Presence of aortocoronary bypass graft" },
    { code: "Z95.5", description: "Presence of coronary angioplasty implant and graft" },
    { code: "Z96.1", description: "Presence of intraocular lens" },
    { code: "Z96.41", description: "Presence of insulin pump (external) (internal)" },
    { code: "Z96.641", description: "Presence of right artificial hip joint" },
    { code: "Z96.642", description: "Presence of left artificial hip joint" },
    { code: "Z96.651", description: "Presence of right artificial knee joint" },
    { code: "Z96.652", description: "Presence of left artificial knee joint" },
    { code: "Z97.1", description: "Presence of artificial limb (complete) (partial)" },
    { code: "Z99.11", description: "Dependence on respirator [ventilator] status" },
    { code: "Z99.2", description: "Dependence on renal dialysis" },
    { code: "Z99.3", description: "Dependence on wheelchair" },
    { code: "Z99.81", description: "Dependence on supplemental oxygen" }
];

/**
 * Search the local ICD-10 code list.
 * Matches against both code and description (case-insensitive).
 */
export function searchLocalICD10(query: string, maxResults = 20): ICD10Code[] {
    if (!query || query.length < 2) {
        return [];
    }

    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);

    const scored = COMMON_ICD10_CODES.map((entry) => {
        const haystack = `${entry.code} ${entry.description}`.toLowerCase();
        let score = 0;
        for (const term of terms) {
            if (haystack.includes(term)) {
                score++;
            }
            // Bonus for code prefix match
            if (entry.code.toLowerCase().startsWith(term)) {
                score += 2;
            }
        }
        return { entry, score };
    })
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults);

    return scored.map((s) => s.entry);
}
